import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { TrainedModel } from "@/lib/ml";

export interface PredictiveModelRow {
  id: string;
  name: string;
  model_type: string | null;
  industry: string | null;
  target_variable: string | null;
  data_source: string | null;
  status: string | null;
  training_progress: number | null;
  accuracy: number | null;
  predictions: any;
  parameters: any;
  metrics: any;
  feature_importance: any;
  training_data: any;
  coefficients: any;
  deployment_status: string;
  deployed_at: string | null;
  endpoint_token: string | null;
  created_at: string;
  updated_at: string;
}

/** CRUD + training persistence for `predictive_models`. */
export const useModels = () => {
  const queryClient = useQueryClient();

  const { data: models = [], isLoading } = useQuery({
    queryKey: ["predictive-models"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase
        .from("predictive_models")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as PredictiveModelRow[];
    },
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["predictive-models"] });

  const saveTrainedModel = useMutation({
    mutationFn: async (input: {
      id?: string;
      name: string;
      industry?: string;
      dataSource: string;
      parameters: Record<string, any>;
      trained: TrainedModel;
      sampleRows?: any[];
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Please sign in to save a model.");

      const payload = {
        user_id: user.id,
        name: input.name,
        model_type: input.trained.modelType,
        industry: input.industry ?? null,
        target_variable: input.trained.target,
        data_source: input.dataSource,
        status: "trained",
        training_progress: 100,
        accuracy: input.trained.metrics.accuracy ?? input.trained.metrics.r2 ?? null,
        metrics: {
          ...input.trained.metrics,
          train_rows: input.trained.trainRows,
          test_rows: input.trained.testRows,
        } as any,
        feature_importance: input.trained.featureImportance as any,
        coefficients: {
          intercept: input.trained.intercept,
          weights: input.trained.coefficients,
          clusters: input.trained.clusters ?? null,
        } as any,
        predictions: input.trained.predictions as any,
        parameters: { ...input.parameters, features: input.trained.features } as any,
        training_data: (input.sampleRows ?? []).slice(0, 100) as any,
      };

      if (input.id) {
        const { data, error } = await supabase
          .from("predictive_models")
          .update(payload)
          .eq("id", input.id)
          .eq("user_id", user.id)
          .select()
          .single();
        if (error) throw error;
        return data as unknown as PredictiveModelRow;
      }

      const { data, error } = await supabase
        .from("predictive_models")
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as PredictiveModelRow;
    },
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.message || "Could not save the model"),
  });

  const deployModel = useMutation({
    mutationFn: async (params: { id: string; deploy: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Please sign in.");
      const token = params.deploy ? crypto.randomUUID().replace(/-/g, "") : null;
      const { data, error } = await supabase
        .from("predictive_models")
        .update({
          deployment_status: params.deploy ? "deployed" : "offline",
          deployed_at: params.deploy ? new Date().toISOString() : null,
          endpoint_token: token,
        })
        .eq("id", params.id)
        .eq("user_id", user.id)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as PredictiveModelRow;
    },
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.message || "Could not change deployment"),
  });

  const deleteModel = useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Please sign in.");
      const { error } = await supabase
        .from("predictive_models")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { models, isLoading, saveTrainedModel, deployModel, deleteModel, refresh: invalidate };
};
